import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Template picker list for the Export dialog + full metadata list / upload /
// delete hooks used by the Templates settings panel. The /for-export slice is
// gated on export_treatment_plans so BCBAs without settings-management can
// still choose a template; everything else requires manage_settings.

export interface TemplateOption {
    id: string;
    name: string;
}

export interface TpTemplate {
    id: string;
    name: string;
    fileName: string;
    fileSize: number;
    uploadedBy: string | null;
    uploadedByName: string;
    uploadedAt: string;
    isSystem: boolean;
}

async function fetchTemplatesForExport(): Promise<TemplateOption[]> {
    const res = await api.get<TemplateOption[]>('/tp-templates/for-export');
    return res.data;
}

export function useTemplatesForExport(enabled: boolean = true) {
    return useQuery({
        queryKey: ['tp-templates', 'for-export'] as const,
        queryFn: fetchTemplatesForExport,
        enabled,
        staleTime: 60_000,
    });
}

async function fetchTemplates(): Promise<TpTemplate[]> {
    const res = await api.get<TpTemplate[]>('/tp-templates');
    return res.data;
}

export function useTemplates(enabled: boolean = true) {
    return useQuery({
        queryKey: ['tp-templates', 'list'] as const,
        queryFn: fetchTemplates,
        enabled,
        staleTime: 30_000,
    });
}

export interface UploadTemplatePayload {
    file: File;
    name?: string;
    uploaderName?: string;
}

export function useUploadTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: UploadTemplatePayload) => {
            const form = new FormData();
            form.append('file', payload.file);
            if (payload.name) form.append('name', payload.name);
            if (payload.uploaderName) form.append('uploaderName', payload.uploaderName);
            const res = await api.post<TpTemplate>('/tp-templates', form);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tp-templates'] });
        },
    });
}

export function useDeleteTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/tp-templates/${id}`);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tp-templates'] });
        },
    });
}

// Reference catalog used by the "Placeholder reference" dialog. Server
// merges the static spec with live custom-field slugs for the practice.

export interface PlaceholderScalar {
    group: string;
    key: string;
    description: string;
}

export interface PlaceholderField {
    key: string;
    description: string;
}

export interface PlaceholderLoop {
    key: string;
    description: string;
    fields: PlaceholderField[];
    nested?: Array<{
        key: string;
        description: string;
        fields: PlaceholderField[];
        images?: PlaceholderField[];
    }>;
}

export interface PlaceholderCustomField {
    key: string;
    label: string;
    description: string;
}

export interface PlaceholderCatalog {
    scalars: PlaceholderScalar[];
    loops: PlaceholderLoop[];
    images: PlaceholderField[];
    customFields: PlaceholderCustomField[];
}

async function fetchPlaceholderCatalog(): Promise<PlaceholderCatalog> {
    const res = await api.get<PlaceholderCatalog>('/tp-templates/placeholders');
    return res.data;
}

export function usePlaceholderCatalog(enabled: boolean = true) {
    return useQuery({
        queryKey: ['tp-templates', 'placeholders'] as const,
        queryFn: fetchPlaceholderCatalog,
        enabled,
        staleTime: 60_000,
    });
}

// Download a template's raw .docx. Gated server-side on view_treatment_plans.
export async function downloadTemplateFile(
    id: string,
    fallbackName: string,
): Promise<{ blob: Blob; filename: string }> {
    const res = await api.get(`/tp-templates/${id}/file`, { responseType: 'blob' });
    const cd = String(res.headers['content-disposition'] ?? '');
    const match = /filename="?([^"]+)"?/i.exec(cd);
    const filename = match ? match[1] : fallbackName;
    return { blob: res.data as Blob, filename };
}
